/**
 * TextField Component for training records
 * @param recordName string,
 *        link string,
 *        input string,
 *        onEditMode boolean to check if the UI should be editable
 *
 */

import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CreateIcon from '@mui/icons-material/Create';
import ITooltip from '../ITooltip';
import Text from '../Text';
import CustomTextField from './CustomTextField';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import {
  validateRecordName,
  validateRecordURL,
} from '../../../services/util/editChecks';

const useStyles = makeStyles({
  buttonDiv: {
    alignSelf: 'flex-end',
    display: 'flex',
  },
  root: {
    position: 'relative',
    marginRight: '10px',

    '& .MuiFormControl-root': {
      margin: '10px 0 5px',
      width: '100%',
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
  },
  fieldRoot: {
    border: `1px solid rgba(0, 0, 0, 0.23)`, ////// HARDCODED TO USE CONSTS - to be Assigned a palette color
    borderRadius: '4px',
    display: 'flex',
    height: '26px',
    justifyContent: 'space-between',
    padding: '0 10px',

    '& p': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: '165px',
      height: '100%',
      textOverflow: 'ellipsis',
    },

    '& a': {
      display: 'inline-block',
      width: '95%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    '& svg': {
      display: 'none',
      width: '15px',
    },

    '&:hover': {
      borderColor: 'rgb(26, 115, 232)', ////// HARDCODED TO USE CONSTS - to be Assigned a palette color
      cursor: 'pointer',

      '& svg': {
        display: 'block',
      },
    },
  },
  documentLink: {
    textDecoration: 'underline',
  },
  popupRoot: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 18px',
  },
  paper: {
    position: 'absolute',
    zIndex: 2,
    backgroundColor: 'white', ////// HARDCODED TO USE CONSTS - to be Assigned a palette color
  },
});

export default function RecordField({
  onEditMode,
  record,
  setRecord,
  updateEditTrainingRecords,
}) {
  const classes = useStyles();
  const recordName = record.recordName;
  const recordURL = record.url;
  const [originalName, setOriginalName] = useState(recordName);
  const [originalURL, setOriginalURL] = useState(recordURL);
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);

  const setRecordName = (input) => {
    setRecord({
      ...record,
      recordName: input,
    });
  };

  const setRecordURL = (input) => {
    setRecord({
      ...record,
      url: input,
    });
  };

  const openHandler = () => {
    setIsOpen(true);
  };

  const cancelHandler = () => {
    setRecord({
      ...record,
      recordName: originalName,
      url: originalURL,
    });
    setIsOpen(false);
  };

  const saveHandler = () => {
    setOriginalName(recordName);
    setOriginalURL(recordURL);
    updateEditTrainingRecords(record.trainingRecordID, record);
    setIsOpen(false);
  };

  useEffect(() => {
    const recordNameHasError = validateRecordName(recordName.value);
    const recordUrlHasError = validateRecordURL(recordURL.value);

    if (recordNameHasError || recordUrlHasError) setHasError(true);
    else setHasError(false);

    // XXX Fix this in the future
    // eslint-disable-next-line
  }, [record]);

  return (
    <div className={classes.root}>
      {!onEditMode ? (
        recordURL.value === '/' ? (
          <p>{recordName.value}</p>
        ) : (
          <a
            className={classes.documentLink}
            href={recordURL.value}
            target='_blank'
            rel='noreferrer'
          >
            {recordName.value}
          </a>
        )
      ) : (
        <>
          <div className={classes.fieldRoot} onClick={openHandler}>
            <p>{recordName.value}</p>
            <CreateIcon />
          </div>

          {isOpen && (
            <Paper className={classes.paper} elevation={3}>
              <div className={classes.popupRoot}>
                <Text type='subHeading'>Edit Link</Text>
                <CustomTextField
                  input={recordName}
                  placeholder={'No description to show'}
                  onChange={setRecordName}
                  onEditMode={true}
                  validator={validateRecordName(recordName.value)}
                />
                <CustomTextField
                  input={recordURL}
                  placeholder={'No description to show'}
                  onChange={setRecordURL}
                  onEditMode={true}
                  validator={validateRecordURL(recordURL.value)}
                />

                <div className={classes.buttonDiv}>
                  <ITooltip content='Cancel' placement='top'>
                    <IconButton aria-label='Cancel' onClick={cancelHandler} style={{ padding: 12 }}>
                      {' '}
                      <CloseIcon />{' '}
                    </IconButton>
                  </ITooltip>

                  <ITooltip content='Save' placement='top'>
                    <IconButton aria-label='Save' disabled={hasError} onClick={saveHandler} style={{ padding: 12 }}>
                      {' '}
                      <CheckCircleIcon />{' '}
                    </IconButton>
                  </ITooltip>
                </div>
              </div>
            </Paper>
          )}
        </>
      )}
    </div>
  );
}
